import { useState } from 'react';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { ProductOrderBody, ProductView, ProductViewWithLogEvents, SettingSetView } from '@lemoncloud/codes-goods-api';
import { Params, createAsyncDelay } from '@lemoncloud/lemon-web-core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { createQueryKeys } from '@codes/base-api';
import { useCustomMutation } from '@codes/shared';

import {
    approveProduct,
    autoDeployProduct,
    cancelProduct,
    createProduct,
    deleteProduct,
    fetchChildProducts,
    fetchProductById,
    fetchProductSettingsById,
    fetchProducts,
    fetchProductsByProjectId,
    orderCatalogs,
    updateProduct,
    updateProductAtAdmin,
    updateProductSettings,
} from '../apis';
import { CREATE_PRODUCT_KEY, ORDER_CATALOGS_KEY, UPDATE_PRODUCT_AT_ADMIN_KEY, UPDATE_PRODUCT_KEY } from '../consts';
import {
    ApproveProductDTO,
    AutoDeployProductDTO,
    CreateProductBody,
    HandleErrorEventFunc,
    HandleSuccessEventFunc,
    PaginationType,
    UpdateProductBody,
    UpdateProductSettingsDTO,
} from '../types';

export const productSettingsKey = createQueryKeys('product-settings');

export const useOrderCatalogs = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [ORDER_CATALOGS_KEY],
        mutationFn: async (body: ProductOrderBody) => {
            const response = await orderCatalogs(body);
            await createAsyncDelay(1000);
            return response;
        },
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useCreateProduct = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [CREATE_PRODUCT_KEY],
        mutationFn: async (body: CreateProductBody) => await createProduct(body),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useUpdateProduct = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [UPDATE_PRODUCT_KEY],
        mutationFn: async (body: UpdateProductBody) => await updateProduct(body),
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const useUpdateProductAtAdmin = (
    handleSuccessEvent?: HandleSuccessEventFunc<ProductView>,
    handleErrorEvent?: HandleErrorEventFunc
) =>
    useMutation({
        mutationKey: [UPDATE_PRODUCT_AT_ADMIN_KEY],
        mutationFn: async ({
            updateProductBody,
            autoDeployProductDTO,
        }: {
            updateProductBody: UpdateProductBody;
            autoDeployProductDTO?: AutoDeployProductDTO;
        }) => {
            const isAssigned = updateProductBody.body?.state === 'assigned';

            if (isAssigned && autoDeployProductDTO) {
                await autoDeployProduct(autoDeployProductDTO);
            }

            const response = await updateProductAtAdmin(updateProductBody);

            return response;
        },
        onSuccess(data) {
            handleSuccessEvent && handleSuccessEvent(data);
        },
        onError(error) {
            handleErrorEvent && handleErrorEvent(error);
        },
    });

export const productsKey = {
    ...createQueryKeys('products'),
    children: (parentId?: string) => ['products', 'children', parentId] as const,
};

export const useFetchProductsByProjectId = (project: string, params: Params = {}) =>
    useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.list({ projectId: project, ...params }),
        queryFn: async () => {
            const result = await fetchProductsByProjectId(project, params);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        refetchOnWindowFocus: false,
        enabled: !!project,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 10000,
    });

export const useFetchProducts = (params: Params) =>
    useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.list({ ...params }),
        queryFn: async () => {
            const result = await fetchProducts(params);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: 0,
    });

export const useFetchProductById = (productId?: string) => {
    return useQuery<ProductView>({
        queryKey: productsKey.detail(productId as string),
        queryFn: () => fetchProductById(productId),
        refetchOnWindowFocus: false,
        enabled: !!productId,
        refetchOnMount: true,
        staleTime: 0,
    });
};

export const useProductPolling = (productId?: string) => {
    return useQuery<ProductViewWithLogEvents>({
        queryKey: productsKey.detail(productId as string),
        queryFn: () => fetchProductById(productId),
        refetchOnWindowFocus: false,
        enabled: !!productId,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 1000 * 5, // 5초마다 갱신,
    });
};

export const useDeleteProduct = () => {
    return useCustomMutation<ProductView, AxiosError<string>, string>((productId: string) => deleteProduct(productId), {
        onError: error => {
            toast({ title: `${error.response?.data}` });
        },
    });
};

export const useApproveProduct = () => {
    return useCustomMutation((dto: ApproveProductDTO) => approveProduct(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useCancelProduct = () => {
    return useCustomMutation((productId: string) => cancelProduct(productId), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useAutoDeployProduct = () => {
    const [currentProductId, setCurrentProductId] = useState<string | null>(null);

    const mutation = useCustomMutation(
        async (productId: string) => {
            setCurrentProductId(productId); // 처리 중인 ID 저장
            const response = await autoDeployProduct({ productId });
            return response;
        },
        {
            onError: error => {
                toast({
                    title: error instanceof Error ? error.message : 'An unknown error occurred',
                });
            },
            onSettled: () => {
                setCurrentProductId(null); // 처리 완료 후 초기화
            },
        }
    );

    return {
        ...mutation,
        currentProductId,
    };
};

export const useProductSettingsById = (productId?: string) => {
    return useQuery<SettingSetView[]>({
        queryKey: productSettingsKey.detail(productId as string),
        queryFn: () => fetchProductSettingsById(productId),
        refetchOnWindowFocus: false,
        enabled: !!productId,
        refetchOnMount: true,
        staleTime: 0,
    });
};

export const useUpdateProductSettings = () => {
    return useCustomMutation((dto: UpdateProductSettingsDTO) => updateProductSettings(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useFetchChildProducts = (parentProductId?: string) =>
    useQuery<PaginationType<ProductView[]>>({
        queryKey: productsKey.children(parentProductId),
        queryFn: async () => {
            if (!parentProductId) {
                throw new Error('parentProductId is required');
            }
            const result = await fetchChildProducts(parentProductId);
            return { ...result, data: result.list || [] } as PaginationType<ProductView[]>;
        },
        enabled: !!parentProductId,
        refetchOnMount: true,
        staleTime: 0,
        refetchInterval: 10000,
    });
